import { RegistroCta } from '@/features/auth/components/registro-cta';
import {
  Building2,
  Globe,
  Bell,
  ShoppingCart,
  FileText,
  ArrowRight,
  CheckCircle2,
  ArrowRightLeft,
  Server,
  Lock,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { DocsLayout } from '../components/docs-layout';

export function IntegrationsPage() {
  return (
    <DocsLayout
      title="Integraciones"
      description="Conecta Prixia con tus sistemas existentes para maximizar la productividad de tu equipo."
    >
      <div className="space-y-12">
        {/* SAP Integration */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Integracion SAP</h2>
          </div>

          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Prixia se integra nativamente con SAP para sincronizar datos maestros y mantener la
                consistencia entre sistemas.
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    SAP Material Master
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Sincronizacion automatica de materiales, incluyendo codigos, descripciones,
                    categorias y datos arancelarios.
                  </p>
                  <ul className="space-y-1.5">
                    {[
                      'Sincronizacion bidireccional de datos de materiales',
                      'Mapeo automatico de campos entre SAP y PI',
                      'Historial de sincronizaciones con logs detallados',
                      'Programacion de sincronizacion automatica via BullMQ',
                    ].map((item, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400"
                      >
                        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    SAP Cloud for Customer (C4C)
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Vincula cotizaciones con oportunidades de venta en SAP C4C para un seguimiento
                    completo del pipeline.
                  </p>
                  <ul className="space-y-1.5">
                    {[
                      'Campo ID Oportunidad C4C en cotizaciones',
                      'Sincronizacion de clientes y contactos',
                      'Actualizacion de estado de oportunidades',
                      'Protocolo OData para comunicacion segura',
                    ].map((item, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400"
                      >
                        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* API REST */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-400 flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">API REST</h2>
          </div>

          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Prixia expone una API REST completa que permite integrar la plataforma con cualquier
                sistema externo.
              </p>

              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
                  <Lock className="h-5 w-5 text-violet-500 mb-2" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Autenticacion JWT
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Tokens de acceso y refresco con expiracion configurable. Comunicacion cifrada
                    via HTTPS.
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
                  <Server className="h-5 w-5 text-violet-500 mb-2" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Endpoints RESTful
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    CRUD completo para cotizaciones, modelos, materiales, clientes y datos maestros.
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
                  <ArrowRightLeft className="h-5 w-5 text-violet-500 mb-2" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Validacion Zod
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Todos los endpoints validan los datos de entrada con esquemas Zod para
                    garantizar consistencia.
                  </p>
                </div>
              </div>

              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Endpoints Principales
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-800">
                      <th className="pb-2 pr-4 text-left font-medium text-gray-500 dark:text-gray-400">
                        Recurso
                      </th>
                      <th className="pb-2 pr-4 text-left font-medium text-gray-500 dark:text-gray-400">
                        Ruta Base
                      </th>
                      <th className="pb-2 text-left font-medium text-gray-500 dark:text-gray-400">
                        Operaciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600 dark:text-gray-400">
                    {[
                      {
                        resource: 'Autenticacion',
                        path: '/api/auth',
                        ops: 'Login, Registro, Refresco de token',
                      },
                      {
                        resource: 'Cotizaciones',
                        path: '/api/quotes',
                        ops: 'CRUD, Enviar, Clonar, PDF',
                      },
                      {
                        resource: 'Modelos de Pricing',
                        path: '/api/pricing-models',
                        ops: 'CRUD, Variables',
                      },
                      { resource: 'Materiales', path: '/api/materials', ops: 'CRUD, Aranceles' },
                      {
                        resource: 'Datos Maestros',
                        path: '/api/master-data',
                        ops: 'Divisas, LOBs, Paises',
                      },
                      {
                        resource: 'Aprobaciones',
                        path: '/api/approvals',
                        ops: 'Listar, Aprobar, Rechazar, Ajustar',
                      },
                      { resource: 'Clientes', path: '/api/customers', ops: 'CRUD, Historial' },
                      {
                        resource: 'Dashboard',
                        path: '/api/dashboard',
                        ops: 'Metricas, Estadisticas',
                      },
                      {
                        resource: 'Asistente IA',
                        path: '/api/ai-assistant',
                        ops: 'Chat, Consultas',
                      },
                    ].map((row, idx) => (
                      <tr
                        key={idx}
                        className="border-b border-gray-100 dark:border-gray-800/50 last:border-0"
                      >
                        <td className="py-2 pr-4 font-medium text-gray-900 dark:text-white">
                          {row.resource}
                        </td>
                        <td className="py-2 pr-4 font-mono text-xs">{row.path}</td>
                        <td className="py-2">{row.ops}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Webhooks */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-400 flex items-center justify-center">
              <Bell className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Webhooks</h2>
          </div>

          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Recibe notificaciones en tiempo real cuando ocurren eventos importantes en Prixia.
                Configura URLs de destino para los eventos que te interesen.
              </p>

              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Eventos Disponibles
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { event: 'quote.created', desc: 'Se crea una nueva cotizacion' },
                  { event: 'quote.submitted', desc: 'Cotizacion enviada para aprobacion' },
                  { event: 'quote.approved', desc: 'Cotizacion aprobada por el aprobador' },
                  { event: 'quote.rejected', desc: 'Cotizacion rechazada' },
                  { event: 'quote.adjustment_requested', desc: 'Aprobador solicita ajuste' },
                  { event: 'quote.expired', desc: 'Cotizacion vencida por tiempo' },
                  { event: 'material.updated', desc: 'Cambio en datos de material' },
                  { event: 'user.created', desc: 'Nuevo usuario registrado' },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-800"
                  >
                    <code className="text-xs font-mono px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-emerald-600 dark:text-emerald-400 shrink-0">
                      {item.event}
                    </code>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{item.desc}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* E-commerce Connectors */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Conectores E-commerce
            </h2>
          </div>

          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Monitorea precios de competidores en las principales plataformas de e-commerce con
                scrapers automatizados. Los datos se recopilan periodicamente y se almacenan para
                analisis comparativo.
              </p>

              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  {
                    name: 'Shopify',
                    desc: 'Extraccion de precios y disponibilidad de tiendas Shopify. Soporte para variantes de producto y precios con descuento.',
                  },
                  {
                    name: 'VTEX',
                    desc: 'Monitoreo de catalogos VTEX incluyendo precios de lista, precios promocionales y stock disponible.',
                  },
                  {
                    name: 'Magento',
                    desc: 'Scraping de tiendas Magento con soporte para productos configurables, precios por grupo y ofertas especiales.',
                  },
                  {
                    name: 'Dismac',
                    desc: 'Conector especializado para la plataforma Dismac, extrayendo precios mayoristas y minoristas.',
                  },
                  {
                    name: 'Elgeniox',
                    desc: 'Integracion con el catalogo de Elgeniox para monitoreo de precios en tiempo real.',
                  },
                  {
                    name: 'Scraper Generico',
                    desc: 'Scraper basado en Puppeteer para cualquier sitio web con configuracion flexible de selectores CSS.',
                  },
                ].map((platform, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-lg border border-gray-200 dark:border-gray-800 hover:border-orange-200 dark:hover:border-orange-800 transition-colors"
                  >
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {platform.name}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{platform.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* PDF Export */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-400 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Exportacion PDF</h2>
          </div>

          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Genera documentos PDF profesionales de tus cotizaciones para compartir con clientes.
                El PDF incluye toda la informacion relevante formateada para una presentacion
                impecable.
              </p>
              <ul className="space-y-2">
                {[
                  'Datos completos del cliente y la empresa emisora.',
                  'Detalle de cada seccion con items, cantidades y precios.',
                  'Cascada de costos con margenes y totales.',
                  'Informacion de validez y condiciones comerciales.',
                  'Formato profesional listo para presentar a clientes.',
                  'Generacion en servidor, compatible con cualquier navegador.',
                ].map((item, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400"
                  >
                    <CheckCircle2 className="mt-0.5 h-4 w-4 text-pink-500 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* Architecture Diagram */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-400 flex items-center justify-center">
              <ArrowRightLeft className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Arquitectura de Integraciones
            </h2>
          </div>

          <Card>
            <CardContent className="p-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                {/* External systems */}
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Sistemas Externos
                  </h3>
                  {['SAP ERP', 'SAP C4C', 'Shopify', 'VTEX', 'Magento', 'Webhooks'].map((sys) => (
                    <div
                      key={sys}
                      className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300"
                    >
                      {sys}
                    </div>
                  ))}
                </div>

                {/* Prixia */}
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-blue-500 uppercase tracking-wider">
                    Prixia
                  </h3>
                  <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-violet-50 dark:from-blue-950/30 dark:to-violet-950/30 border border-blue-200 dark:border-blue-800 space-y-2">
                    {[
                      'API REST',
                      'Worker (BullMQ)',
                      'Scrapers',
                      'OData Client',
                      'PDF Generator',
                    ].map((comp) => (
                      <div
                        key={comp}
                        className="px-2 py-1.5 rounded bg-white dark:bg-gray-950 border border-blue-100 dark:border-blue-900/50 text-xs font-medium text-blue-600 dark:text-blue-400"
                      >
                        {comp}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Data stores */}
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Almacenamiento
                  </h3>
                  {['PostgreSQL', 'Redis Cache', 'Claude AI API'].map((store) => (
                    <div
                      key={store}
                      className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300"
                    >
                      {store}
                    </div>
                  ))}
                </div>
              </div>

              <p className="mt-6 text-xs text-gray-500 dark:text-gray-400 text-center">
                Los scrapers se ejecutan como jobs de BullMQ en un worker independiente. Las
                sincronizaciones SAP usan el protocolo OData. La API REST valida todas las entradas
                con Zod.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <div className="text-center py-12 px-6 rounded-2xl bg-gradient-to-r from-blue-50 to-violet-50 dark:from-blue-950/30 dark:to-violet-950/30 border border-blue-100 dark:border-blue-900/50">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Necesitas una integracion personalizada?
          </h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
            Nuestro equipo puede ayudarte a conectar Prixia con cualquier sistema de tu empresa.
          </p>
          <RegistroCta
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-3 rounded-lg hover:shadow-lg transition-shadow"
          >
            Contactar al Equipo
            <ArrowRight className="w-4 h-4" />
          </RegistroCta>
        </div>
      </div>
    </DocsLayout>
  );
}
